/**
 * Per-advisor branding: the five values in `SYNCED_SETTINGS` (app.html:5506)
 * turned into CSS custom properties.
 *
 * Pure, apart from one function that takes the element to write to as an
 * argument. No Firebase, no `document`, no localStorage — the Account screen
 * owns reading and writing; this file only knows what a brand IS.
 *
 * ── The three legacy bugs this replaces ───────────────────────────────────
 * `applyBranding()` (app.html:8816) is the shipped implementation. It has
 * three defects, each with a regression test in `tests/unit/brand.test.js`
 * naming it:
 *
 *   1. ACCENT IS UNREACHABLE. `saveBrandSettings()` (app.html:8807–8813)
 *      passes the PRIMARY colour into the accent parameter — the same
 *      `getElementById('brandColorPrimary').value` twice — and never writes
 *      `mbm_brandColorAccent`. The key is in SYNCED_SETTINGS, syncs across
 *      devices, and cannot be given a value by any control in the product.
 *      Here accent is its own field that DEFAULTS to primary, so today's
 *      behaviour is reproduced and the key becomes usable.
 *
 *   2. THE BODY FONT IS IGNORED. `applyBranding(primary, accent, text,
 *      headingFont, bodyFont)` accepts `bodyFont` and never reads it — it
 *      sets `font-family: <headingFont>, serif` on the root element, so
 *      choosing "Inter" for body text set every heading AND every body string
 *      in the app to Inter, and choosing a body font did nothing at all.
 *
 *   3. AN UNVALIDATED VALUE REACHES A STYLE. The colour comes from a hidden
 *      input and the font from a <select>, and both are written to
 *      `style.setProperty` unchecked. A synced settings document is not a
 *      trusted input — it is a string that arrived over the network. Every
 *      value here is validated against a shape or an allow-list, and anything
 *      that fails falls back to the default rather than being passed through.
 */

/** The settings keys, exactly as the legacy app spells them. */
export const BRAND_KEYS = Object.freeze({
  /* Not colours, and not CSS. Both are printed on what a client receives —
     the agency name at the head of an exported itinerary, the logo on the
     public intake page — which is why they belong with the brand rather than
     with the account profile. Added 19 Aug 2026 (Settings pass 2); the
     original has both in the same White-Label Branding block
     (app.html:4533, 4538). */
  agencyName: 'mbm_brandAgencyName',
  /* A number a client can ring. It sits with the brand rather than in the
     account profile for the reason the two above do: it is printed on what a
     client receives — the closing page of every exported packet — and not on
     anything an advisor sees while working. Added 5 Sep 2026 with the packet
     themes, whose closing page is a contact card and was reading as an odd one
     without it. */
  agencyPhone: 'mbm_brandAgencyPhone',
  logo: 'mbm_brandLogoUrl',
  primary: 'mbm_brandColorPrimary',
  accent: 'mbm_brandColorAccent',
  text: 'mbm_brandColorText',
  heading: 'mbm_brandFontHeading',
  body: 'mbm_brandFontBody',
  /* Whether the logo may be used on social exports. A separate switch because
     it is a separate decision — see `lib/agency.js#setLogoOnSocial`. New in
     /v2; the original has no such option because its social export carries no
     logo at all. */
  logoOnSocial: 'mbm_brandLogoOnSocial',
})

/** How long an agency name may be. Long enough for a real one, short enough
    not to break the head of an exported itinerary. */
export const MAX_AGENCY_NAME = 60

/**
 * The defaults `loadBrandSettings()` applies (app.html:8780–8783). Keep them
 * identical: they are what an advisor who has never opened the branding panel
 * is looking at right now.
 */
export const BRAND_DEFAULTS = Object.freeze({
  agencyName: '',
  agencyPhone: '',
  logoOnSocial: true,
  logo: '',
  primary: '#B8A0D8',
  accent: '', // empty means "same as primary" — see bug 1 above
  text: '#3A3A3A',
  heading: 'Cormorant Garamond',
  body: 'Quicksand',
})

/** Heading faces the Account screen offers (app.html:4579–4586). */
export const HEADING_FONTS = Object.freeze([
  'Cormorant Garamond',
  'Playfair Display',
  'Lora',
  'Merriweather',
  'Libre Baskerville',
  'DM Serif Display',
])

/** Body faces the Account screen offers (app.html:4590–4597). */
export const BODY_FONTS = Object.freeze([
  'Quicksand',
  'Inter',
  'Nunito',
  'Poppins',
  'Raleway',
  'Open Sans',
])

/** Fallbacks appended to a chosen face, so a missing webfont degrades well. */
const SERIF_TAIL = "Georgia, 'Times New Roman', serif"
const SANS_TAIL = "ui-sans-serif, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"

/** The custom properties this module owns. Nothing else may write them. */
export const BRAND_PROPS = Object.freeze([
  '--brand-primary',
  '--brand-accent',
  '--brand-text',
  '--brand-font-heading',
  '--brand-font-body',
])

/**
 * The three that are COLOURS, and are therefore suppressed in Slate.
 *
 * Slate defines its own palette on `body.theme-slate` — `--charcoal` becomes
 * `#eceae5`, and `--ink` resolves through `--brand-text` to it. An inline
 * `--brand-text: #3a3a3a` on the root element beats that, so an advisor's
 * light-theme text colour lands on a dark page and every menu row goes dim.
 * Trey, 20 Aug 2026, with a screenshot of exactly that: "the text in the
 * hamburger menu is dim."
 *
 * Legacy already knows: `applyBranding` (app.html:8828-8836) applies the three
 * colours only when `currentTheme !== 'slate'` and REMOVES them otherwise,
 * with a comment saying why. That guard did not make it into the port.
 *
 * The two FONTS are not here, because legacy applies those in both themes
 * (8839: "Fonts still apply in both themes") and a typeface has no contrast
 * to get wrong.
 */
export const BRAND_COLOUR_PROPS = Object.freeze(['--brand-primary', '--brand-accent', '--brand-text'])

/**
 * @typedef {{agencyName: string, agencyPhone: string, logo: string, logoOnSocial: boolean, primary: string,
 *   accent: string, text: string, heading: string, body: string}} Brand
 */

/**
 * A logo URL that is safe to put in a `src`.
 *
 * `https:` and `data:image/…` only. The legacy field takes anything typed
 * into it and hands it straight to `img.src` (app.html:8789/8812) — including
 * `javascript:`, which does nothing in a `src` but is stored, synced, and
 * eventually printed into an exported HTML itinerary where the context is not
 * guaranteed to stay an `img`.
 *
 * @param {unknown} value
 * @returns {string} '' when it is not a URL worth keeping
 */
export function normalizeLogo(value) {
  const raw = String(value == null ? '' : value).trim()
  if (!raw) return ''
  if (/^data:image\/(png|jpe?g|gif|webp|svg\+xml);/i.test(raw)) return raw
  if (/^https:\/\/[^\s"'<>]+$/i.test(raw)) return raw
  return ''
}

/** @param {unknown} value @returns {string} */
export function normalizeAgencyName(value) {
  return String(value == null ? '' : value)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_AGENCY_NAME)
}

/**
 * A three- or six-digit hex colour, normalised to lowercase six digits.
 *
 * Deliberately narrow. `rgb()`, `hsl()` and named colours are all valid CSS
 * and none of them can be produced by the colour picker that writes this
 * value, so accepting them only widens what an attacker-controlled settings
 * document can put inside a style declaration.
 *
 * @param {unknown} value
 * @param {string} fallback
 * @returns {string}
 */
export function normalizeHex(value, fallback) {
  const raw = String(value == null ? '' : value).trim()
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(raw)
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase()
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase()
  return fallback
}

/**
 * A font face from the allow-list, or the fallback.
 * @param {unknown} value
 * @param {readonly string[]} allowed
 * @param {string} fallback
 * @returns {string}
 */
export function normalizeFont(value, allowed, fallback) {
  const raw = String(value == null ? '' : value).trim()
  const hit = allowed.find((name) => name.toLowerCase() === raw.toLowerCase())
  return hit || fallback
}

/**
 * Read a brand out of a plain record of setting values — a hydrated settings
 * document, a localStorage snapshot, whatever the caller has. Unknown and
 * malformed values become defaults; nothing throws.
 *
 * @param {Record<string, any>|null|undefined} values - keyed by BRAND_KEYS
 * @returns {Brand}
 */
export function readBrand(values) {
  const get = (/** @type {string} */ key) => (values ? values[key] : undefined)
  const primary = normalizeHex(get(BRAND_KEYS.primary), BRAND_DEFAULTS.primary.toLowerCase())
  return {
    agencyName: normalizeAgencyName(get(BRAND_KEYS.agencyName)),
    /* Trimmed and capped, not validated. Phone numbers are written a dozen
       ways — +81, (281), extensions, "call the office" — and an advisor typing
       what their clients should dial is a better authority on it than a
       pattern. The cap is only there so a paste accident cannot fill a page. */
    agencyPhone: String(get(BRAND_KEYS.agencyPhone) == null ? '' : get(BRAND_KEYS.agencyPhone))
      .trim()
      .slice(0, 40),
    logo: normalizeLogo(get(BRAND_KEYS.logo)),
    /* Absent means ON. A logo an advisor has taken the trouble to upload
       belongs on what they publish unless they say otherwise, and defaulting
       a new switch to off makes it look broken. Stored as the string '0'/'1'
       because that is the shape `/app` writes every boolean setting in. */
    logoOnSocial: get(BRAND_KEYS.logoOnSocial) == null
      ? true
      : String(get(BRAND_KEYS.logoOnSocial)) !== '0',
    primary,
    /* Absent accent means "same as primary" — the shipped behaviour. An
       accent that IS present but malformed also falls back to primary rather
       than to the default lavender, or an advisor with a custom primary would
       get a stranger's colour beside it. */
    accent: normalizeHex(get(BRAND_KEYS.accent), primary),
    text: normalizeHex(get(BRAND_KEYS.text), BRAND_DEFAULTS.text.toLowerCase()),
    heading: normalizeFont(get(BRAND_KEYS.heading), HEADING_FONTS, BRAND_DEFAULTS.heading),
    body: normalizeFont(get(BRAND_KEYS.body), BODY_FONTS, BRAND_DEFAULTS.body),
  }
}

/**
 * Whether a brand is the default one. `applyBrand` uses this to REMOVE the
 * inline properties instead of writing them, so the stylesheet's own defaults
 * win and a future theme can override them. Legacy left stale inline values
 * on the root element when the theme changed and had to remove them by hand
 * (app.html:8825–8829); this makes that state unreachable.
 *
 * @param {Brand} brand
 * @returns {boolean}
 */
export function isDefaultBrand(brand) {
  const d = readBrand(null)
  return (
    brand.agencyName === d.agencyName &&
    brand.logo === d.logo &&
    brand.logoOnSocial === d.logoOnSocial &&
    brand.primary === d.primary &&
    brand.accent === d.accent &&
    brand.text === d.text &&
    brand.heading === d.heading &&
    brand.body === d.body
  )
}

/**
 * The custom properties for a brand. The single source of truth for the
 * mapping documented in `styles/tokens.css` layer 3.
 *
 * @param {Brand} brand
 * @returns {Record<string, string>}
 */
export function brandTokens(brand) {
  return {
    '--brand-primary': brand.primary,
    '--brand-accent': brand.accent,
    '--brand-text': brand.text,
    '--brand-font-heading': `'${brand.heading}', ${SERIF_TAIL}`,
    '--brand-font-body': `'${brand.body}', ${SANS_TAIL}`,
  }
}

/**
 * Write a brand onto an element — normally `document.documentElement`.
 *
 * The element is a parameter rather than a global so this is testable and so
 * a preview can be scoped to one card instead of the whole page, which is
 * what the form builder's live preview needs.
 *
 * ── Colours are suppressed in Slate ──────────────────────────────────────
 *
 * See `BRAND_COLOUR_PROPS`. Slate has its own palette and an inline override
 * on the root element beats it, which is how an advisor's charcoal text
 * colour ends up on a dark page. Legacy guards against this and the port did
 * not. The fonts still apply in both themes.
 *
 * `slate` is a parameter with a sensible default rather than a global read,
 * so this stays testable and so a scoped preview can say which theme it is
 * previewing in.
 *
 * @param {HTMLElement|null|undefined} root
 * @param {Brand} brand
 * @param {{slate?: boolean}} [options]
 * @returns {Record<string, string>} what was written; `{}` when cleared
 */
export function applyBrand(root, brand, options = {}) {
  if (!root || !root.style) return {}

  const slate = options.slate == null ? isSlate(root) : options.slate === true

  if (isDefaultBrand(brand)) {
    for (const prop of BRAND_PROPS) root.style.removeProperty(prop)
    return {}
  }

  /** @type {Record<string, string>} */
  const written = {}
  for (const [prop, value] of Object.entries(brandTokens(brand))) {
    if (slate && BRAND_COLOUR_PROPS.includes(prop)) {
      /* Removed, not merely skipped: switching Grand -> Slate has to take the
         previously-written value with it, or the page keeps whatever was
         inline from before the switch. Legacy makes the same point at 8833. */
      root.style.removeProperty(prop)
      continue
    }
    root.style.setProperty(prop, value)
    written[prop] = value
  }
  return written
}

/**
 * Is the page in Slate? Read off the document the element belongs to, which
 * is where `theme-store.js` puts the class.
 *
 * @param {any} root
 * @returns {boolean}
 */
function isSlate(root) {
  const doc = root && root.ownerDocument
  const body = doc && doc.body
  return Boolean(body && body.classList && body.classList.contains('theme-slate'))
}

/**
 * Serialise a brand back to the settings keys, for the Account screen's save.
 * `accent` is written only when it differs from primary, so the product does
 * not start filling every advisor's synced document with a key that carries
 * no information.
 *
 * @param {Brand} brand
 * @returns {Record<string, string>}
 */
export function writeBrand(brand) {
  /** @type {Record<string, string>} */
  const out = {
    [BRAND_KEYS.agencyName]: brand.agencyName,
    [BRAND_KEYS.logo]: brand.logo,
    [BRAND_KEYS.primary]: brand.primary,
    [BRAND_KEYS.text]: brand.text,
    [BRAND_KEYS.heading]: brand.heading,
    [BRAND_KEYS.body]: brand.body,
    [BRAND_KEYS.logoOnSocial]: brand.logoOnSocial === false ? '0' : '1',
  }
  if (brand.accent && brand.accent !== brand.primary) out[BRAND_KEYS.accent] = brand.accent
  return out
}
